import React, { useState, useMemo } from 'react';
import { useTrades, useBuyHistory, useSellHistory, useToday } from '@/hooks/useDashboardQueries';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';
import { StatusBadge } from '@/components/common/StatusBadge';
import { PnlValue } from '@/components/common/PnlValue';
import { DataTable, Column } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { TableSkeleton, MetricCardSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { formatINR, formatTimestamp } from '@/lib/utils';
import { Trade } from '@/types/trading';
import {
  ArrowLeftRight,
  TrendingUp,
  Activity,
  Search,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle2,
  Calendar,
} from 'lucide-react';

export const TradesView: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<'ALL' | 'BUY' | 'SELL'>('ALL');
  const [outcomeFilter, setOutcomeFilter] = useState<'ALL' | 'WIN' | 'LOSS'>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReasonFilter, setSelectedReasonFilter] = useState<string>('ALL');

  const {
    data: allTradesData,
    isLoading: isAllLoading,
    isError: isAllError,
    error: allError,
    refetch: refetchAll,
  } = useTrades();

  const { data: buyData } = useBuyHistory();
  const { data: sellData } = useSellHistory();
  const { data: todayData } = useToday();

  // Pick dataset based on tab
  const rawTrades = useMemo(() => {
    if (activeSubTab === 'BUY') {
      return buyData?.buy_orders || allTradesData?.trades.filter((t) => t.side === 'BUY') || [];
    }
    if (activeSubTab === 'SELL') {
      return sellData?.sell_orders || allTradesData?.trades.filter((t) => t.side === 'SELL') || [];
    }
    return allTradesData?.trades || [];
  }, [activeSubTab, allTradesData, buyData, sellData]);

  // Extract unique exit/entry reasons for filter
  const uniqueReasons = useMemo(() => {
    const reasons = new Set<string>();
    rawTrades.forEach((t) => {
      if (t.reason) reasons.add(t.reason);
    });
    return Array.from(reasons);
  }, [rawTrades]);

  // Filtered dataset
  const filteredTrades = useMemo(() => {
    return rawTrades.filter((trade) => {
      const matchesSearch =
        trade.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (trade.reason && trade.reason.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesReason =
        selectedReasonFilter === 'ALL' || trade.reason === selectedReasonFilter;
      const matchesOutcome =
        outcomeFilter === 'ALL' ||
        (outcomeFilter === 'WIN' && trade.pnl !== null && trade.pnl !== undefined && trade.pnl > 0) ||
        (outcomeFilter === 'LOSS' && trade.pnl !== null && trade.pnl !== undefined && trade.pnl < 0);
      return matchesSearch && matchesReason && matchesOutcome;
    });
  }, [rawTrades, searchTerm, selectedReasonFilter, outcomeFilter]);

  // Summary Metrics Calculation (Strictly Authoritative)
  const totalRealizedPnl = useMemo(() => {
    return rawTrades.reduce((acc, t) => (t.pnl !== null && t.pnl !== undefined ? acc + t.pnl : acc), 0);
  }, [rawTrades]);

  const winningTrades = useMemo(() => {
    return rawTrades.filter((t) => t.pnl !== null && t.pnl !== undefined && t.pnl > 0).length;
  }, [rawTrades]);

  const losingTrades = useMemo(() => {
    return rawTrades.filter((t) => t.pnl !== null && t.pnl !== undefined && t.pnl < 0).length;
  }, [rawTrades]);

  const completedTradesWithPnl = winningTrades + losingTrades;
  const winRate = completedTradesWithPnl > 0 ? (winningTrades / completedTradesWithPnl) * 100 : null;

  // Table Columns Definition
  const columns: Column<Trade>[] = [
    {
      key: 'symbol',
      header: 'Symbol & Side',
      sortable: true,
      render: (t) => (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <span className="font-bold text-slate-100 font-mono text-sm">{t.symbol}</span>
            <span className="px-1.5 py-0.2 rounded bg-slate-800 text-[10px] font-mono text-slate-400">
              NSE
            </span>
          </div>
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[10px] font-mono font-bold uppercase border ${
              t.side === 'BUY'
                ? 'bg-emerald-950/70 text-emerald-400 border-emerald-800/60'
                : 'bg-rose-950/70 text-rose-400 border-rose-800/60'
            }`}
          >
            {t.side === 'BUY' ? (
              <ArrowUpRight className="w-2.5 h-2.5 text-emerald-400" />
            ) : (
              <ArrowDownRight className="w-2.5 h-2.5 text-rose-400" />
            )}
            {t.side}
          </span>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      sortable: true,
      align: 'right',
      render: (t) => (
        <span className="font-mono text-slate-200 tabular-nums">{t.quantity}</span>
      ),
    },
    {
      key: 'position_value',
      header: 'Position Value',
      sortable: true,
      align: 'right',
      render: (t) => (
        <span className="font-mono text-slate-200 tabular-nums">
          {formatINR(t.quantity * t.price)}
        </span>
      ),
    },
    {
      key: 'price',
      header: 'Fill Price',
      sortable: true,
      align: 'right',
      render: (t) => (
        <span className="font-mono text-slate-200 tabular-nums">{formatINR(t.price)}</span>
      ),
    },
    {
      key: 'pnl',
      header: 'Realized P&L',
      sortable: true,
      align: 'right',
      render: (t) => (
        <PnlValue
          value={t.pnl}
          percent={t.pnl_percent}
          showSign={true}
          showPercent={true}
          layout="stacked"
          align="right"
          nullPlaceholder="—"
        />
      ),
    },
    {
      key: 'order_type',
      header: 'Type',
      align: 'center',
      render: (t) => (
        <span className="text-[10px] font-mono text-slate-400 bg-slate-900 border border-slate-800 px-1.5 py-0.5 rounded uppercase">
          {t.order_type}
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Exit / Execution Reason',
      render: (t) => (
        <span className="text-xs text-slate-300 font-mono max-w-xs truncate block" title={t.reason || '—'}>
          {t.reason || '—'}
        </span>
      ),
    },
    {
      key: 'timestamp',
      header: 'Closed Time',
      sortable: true,
      render: (t) => (
        <span className="font-mono text-slate-400 text-xs flex items-center gap-1.5">
          <Calendar className="w-3 h-3 text-slate-500" />
          {formatTimestamp(t.timestamp, 'compact')}
        </span>
      ),
    },
  ];

  if (isAllLoading && rawTrades.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Executions & Trades"
          icon={ArrowLeftRight}
        />
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>
        <TableSkeleton rows={5} cols={8} />
      </div>
    );
  }

  if (isAllError && rawTrades.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Executions & Trades"
          icon={ArrowLeftRight}
        />
        <ErrorState
          title="Failed to load trade executions"
          message={allError instanceof Error ? allError.message : 'API connection error'}
          onRetry={() => refetchAll()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="animate-stagger-1">
        <PageHeader
          title="Executions & Trades"
          description="Complete execution ledger for paper-mode trade fills, take-profit exits, and stop-loss risk actions."
          icon={ArrowLeftRight}
          badge={
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono px-2.5 py-0.5 rounded-full bg-slate-900 text-slate-300 border border-slate-800">
                {rawTrades.length} TOTAL EXECUTIONS
              </span>
              <StatusBadge status="PAPER_MODE" label="PAPER MODE" size="sm" />
            </div>
          }
        />
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-2">
        <MetricCard
          title={activeSubTab === 'BUY' ? 'Realized P&L (Entries)' : 'Total Realized P&L'}
          value={
            activeSubTab === 'BUY' ? (
              <span className="font-mono text-slate-400 text-lg font-semibold">—</span>
            ) : (
              <PnlValue
                value={totalRealizedPnl}
                size="xl"
                showSign={true}
              />
            )
          }
          subtitle={
            activeSubTab === 'BUY'
              ? `P&L realized upon SELL exit (${rawTrades.length} entries)`
              : `Across ${completedTradesWithPnl} completed exits (${winningTrades}W / ${losingTrades}L)`
          }
          variant={activeSubTab === 'BUY' ? 'default' : totalRealizedPnl > 0 ? 'emerald' : totalRealizedPnl < 0 ? 'crimson' : 'default'}
          icon={TrendingUp}
        />

        <MetricCard
          title="Win Rate"
          value={
            activeSubTab === 'BUY' ? (
              <span className="text-slate-400">—</span>
            ) : winRate !== null ? (
              <span className="text-emerald-400">
                {winRate.toFixed(1)}%
              </span>
            ) : (
              <span className="text-slate-400">—</span>
            )
          }
          subtitle={
            activeSubTab === 'BUY'
              ? 'Evaluated on position exits'
              : completedTradesWithPnl > 0
              ? `${winningTrades} wins / ${losingTrades} losses`
              : 'No completed trades'
          }
          variant={activeSubTab === 'BUY' ? 'default' : 'cyan'}
          icon={Activity}
        />

        <MetricCard
          title="Today Realized"
          value={
            <PnlValue
              value={todayData?.today_pnl || 0}
              size="xl"
              showSign={true}
            />
          }
          subtitle={`${todayData?.trades_count || 0} trades executed today`}
          variant={todayData?.today_pnl && todayData.today_pnl > 0 ? 'emerald' : 'default'}
          icon={CheckCircle2}
        />

        <MetricCard
          title="Active Filter Mode"
          value={activeSubTab}
          subtitle={`Showing ${filteredTrades.length} of ${rawTrades.length} records`}
          variant="default"
          icon={ArrowLeftRight}
        />
      </div>

      {/* Sub-tabs Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 pb-2 animate-stagger-3">
        <div className="flex items-center gap-2 overflow-x-auto">
          <button
            onClick={() => {
              setActiveSubTab('ALL');
              setSelectedReasonFilter('ALL');
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-mono font-medium transition-all duration-150 active:scale-95 ${
              activeSubTab === 'ALL'
                ? 'bg-slate-800 text-slate-100 border border-slate-700 shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
            }`}
          >
            All Trades ({allTradesData?.trades.length || 0})
          </button>
          <button
            onClick={() => {
              setActiveSubTab('SELL');
              setSelectedReasonFilter('ALL');
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-mono font-medium transition-all duration-150 active:scale-95 flex items-center gap-1.5 ${
              activeSubTab === 'SELL'
                ? 'bg-rose-950/80 text-rose-300 border border-rose-800/80 shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
            }`}
          >
            <ArrowDownRight className="w-3.5 h-3.5 text-rose-400" />
            SELL Exits ({allTradesData?.trades.filter((t) => t.side === 'SELL').length || 0})
          </button>
          <button
            onClick={() => {
              setActiveSubTab('BUY');
              setSelectedReasonFilter('ALL');
            }}
            className={`px-3 py-1.5 rounded-md text-xs font-mono font-medium transition-all duration-150 active:scale-95 flex items-center gap-1.5 ${
              activeSubTab === 'BUY'
                ? 'bg-emerald-950/80 text-emerald-300 border border-emerald-800/80 shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
            }`}
          >
            <ArrowUpRight className="w-3.5 h-3.5 text-emerald-400" />
            BUY History ({allTradesData?.trades.filter((t) => t.side === 'BUY').length || 0})
          </button>
        </div>

        {activeSubTab !== 'BUY' && (
          <div className="flex items-center gap-1 text-xs font-mono">
            <button
              onClick={() => setOutcomeFilter('ALL')}
              className={`px-2 py-1 rounded text-[11px] font-mono transition-all ${
                outcomeFilter === 'ALL'
                  ? 'bg-slate-800 text-slate-200 border border-slate-700'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              All Outcomes
            </button>
            <button
              onClick={() => setOutcomeFilter('WIN')}
              className={`px-2 py-1 rounded text-[11px] font-mono transition-all ${
                outcomeFilter === 'WIN'
                  ? 'bg-emerald-950/80 text-emerald-300 border border-emerald-800/70'
                  : 'text-slate-400 hover:text-emerald-400'
              }`}
            >
              Wins Only
            </button>
            <button
              onClick={() => setOutcomeFilter('LOSS')}
              className={`px-2 py-1 rounded text-[11px] font-mono transition-all ${
                outcomeFilter === 'LOSS'
                  ? 'bg-rose-950/80 text-rose-300 border border-rose-800/70'
                  : 'text-slate-400 hover:text-rose-400'
              }`}
            >
              Losses Only
            </button>
          </div>
        )}
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 rounded-lg border border-slate-800 bg-slate-900/40 animate-stagger-3">
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search symbol or reason..."
              className="w-full pl-9 pr-3 py-1.5 text-xs font-mono bg-slate-950/80 border border-slate-800 rounded-md text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all duration-150"
            />
          </div>

          {uniqueReasons.length > 0 && (
            <select
              value={selectedReasonFilter}
              onChange={(e) => setSelectedReasonFilter(e.target.value)}
              className="px-2.5 py-1.5 text-xs font-mono bg-slate-950/80 border border-slate-800 rounded-md text-slate-200 focus:outline-none focus:border-cyan-500 transition-all duration-150"
            >
              <option value="ALL">All Execution Reasons</option>
              {uniqueReasons.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="text-xs font-mono text-slate-400">
          Showing {filteredTrades.length} of {rawTrades.length} executions
        </div>
      </div>

      {/* Desktop Dense Table */}
      <div className="hidden md:block animate-stagger-4">
        <DataTable
          data={filteredTrades}
          columns={columns}
          keyExtractor={(t) => t.id}
          pageSize={10}
          emptyTitle="No closed trades yet."
          emptyDescription="Completed trades will appear here after the bot exits a position."
        />
      </div>

      {/* Mobile / Tablet Responsive Cards */}
      <div className="md:hidden space-y-3 animate-stagger-4">
        {filteredTrades.length === 0 ? (
          <EmptyState
            title="No closed trades yet."
            description="Completed trades will appear here after the bot exits a position."
          />
        ) : (
          filteredTrades.map((t) => {
            const sideClass =
              t.side === 'BUY'
                ? 'card-trade-buy'
                : t.side === 'SELL'
                ? 'card-trade-sell'
                : 'border-slate-800 bg-slate-900/60';

            return (
              <div
                key={t.id}
                className={`p-4 rounded-lg border space-y-3 interactive-card active:scale-[0.99] transition-all duration-150 ${sideClass}`}
              >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-100 font-mono text-base">{t.symbol}</span>
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase border ${
                      t.side === 'BUY'
                        ? 'bg-emerald-950/70 text-emerald-400 border-emerald-800/60'
                        : 'bg-rose-950/70 text-rose-400 border-rose-800/60'
                    }`}
                  >
                    {t.side}
                  </span>
                  <span className="text-[10px] font-mono text-slate-400 bg-slate-900 border border-slate-800 px-1.5 py-0.5 rounded uppercase">
                    {t.order_type || '—'}
                  </span>
                </div>
                <div className="text-right">
                  <PnlValue
                    value={t.pnl}
                    percent={t.pnl_percent}
                    showSign={true}
                    showPercent={true}
                    layout="stacked"
                    align="right"
                    nullPlaceholder="—"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs font-mono pt-2 border-t border-slate-800/60">
                <div>
                  <span className="text-[10px] text-slate-500 block">QUANTITY</span>
                  <span className="text-slate-200 tabular-nums">{t.quantity}</span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] text-slate-500 block">POSITION VALUE</span>
                  <span className="text-slate-200 tabular-nums">{formatINR(t.quantity * t.price)}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-500 block">FILL PRICE</span>
                  <span className="text-slate-200 tabular-nums">{formatINR(t.price)}</span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] text-slate-500 block">CLOSED TIME</span>
                  <span className="text-slate-400 text-[10px] tabular-nums">
                    {formatTimestamp(t.timestamp, 'compact')}
                  </span>
                </div>
              </div>

              {t.reason && (
                <div className="pt-2 border-t border-slate-800/60 text-[11px] font-mono text-slate-400 bg-slate-950/40 p-2 rounded">
                  <span className="text-[10px] font-mono text-slate-500 block">EXIT / EXECUTION REASON</span>
                  <span>{t.reason}</span>
                </div>
              )}
            </div>
          );
        })
      )}
      </div>
    </div>
  );
};
